// ============================================================
// Profileur : une instance de la page Environnement parmi les serveurs mesurés
// ============================================================
// Fonctions pures (testées par profiler.instance.test.ts) de l'onglet « Profileur » de la page d'une
// instance (Déploiement > Environnement > une instance). L'onglet relit les listes que la page
// Profileur lit déjà, une par groupe de jeu, et n'en garde que ce serveur : rien de nouveau n'est
// collecté, rien ne change sur les serveurs.
//
// Rapprochement d'une instance Pterodactyl et d'un serveur du profileur :
// 1. par l'identifiant Pterodactyl. Le mod compagnon envoie P_SERVER_UUID, et l'identifiant court
//    d'une instance (e9e978d6) est le début de cet UUID. Quand le serveur en annonce un, c'est le seul
//    critère : deux serveurs de même nom (faction-1 en pré-prod et en prod, un serveur de Nexus
//    recréé sous le même nom) ne se confondent pas ;
// 2. sans identifiant (serveur lancé hors Pterodactyl), par le nom, celui que NexusMod donne au
//    serveur (mining-15). Sur NationsGlory le groupe peut être dans le nom d'un côté seulement
//    (instance « earth-iris », serveur « earth » du groupe iris) : les deux formes sont acceptées,
//    jamais entre deux groupes différents.
// ============================================================
import type { ServerSummary } from './profiler.types';
import { NEVER_SEEN, serverHealth, type Health } from './profiler.health.ts';
import { serverPath, withConsole, type GroupResult } from './profiler.servers.ts';

/** Instance telle que la page Environnement la connaît. */
export interface InstanceRef {
  /** Identifiant court Pterodactyl (« e9e978d6 »). */
  identifier: string;
  /** Nom de l'instance dans Pterodactyl. */
  name: string;
  /** Groupe de jeu (RECUBE_GROUP) ; absent hors NationsGlory. */
  group?: string | null;
}

/** Adresse de la page Profileur, quand le serveur n'a pas de page à lui. */
export const PROFILER_HOME = '/deploiement/profileur';

function norm(value: string | null | undefined): string | null {
  const text = (value ?? '').trim().toLowerCase();
  return text === '' ? null : text;
}

/** Le nom seul, et le nom suivi de son groupe (« earth », « earth-iris »). */
function nameForms(name: string | null | undefined, group: string | null): string[] {
  const n = norm(name);
  if (!n) return [];
  return group && !n.endsWith(`-${group}`) ? [n, `${n}-${group}`] : [n];
}

/** Ce serveur du profileur est-il cette instance ? */
export function isInstanceServer(server: Pick<ServerSummary, 'id' | 'name' | 'group'>, instance: InstanceRef): boolean {
  const id = norm(server.id);
  if (id) {
    const ident = norm(instance.identifier);
    return ident != null && (id === ident || id.split('-')[0] === ident);
  }
  const serverGroup = norm(server.group);
  const instanceGroup = norm(instance.group);
  if (serverGroup && instanceGroup && serverGroup !== instanceGroup) return false;
  const mine = nameForms(instance.name, instanceGroup);
  return nameForms(server.name, serverGroup).some((n) => mine.includes(n));
}

export interface InstanceMatch {
  /** Groupe de jeu dont le satellite a répondu avec ce serveur. */
  group: GroupResult;
  server: ServerSummary;
  health: Health;
  /** Horloge du satellite. */
  now: number;
}

/**
 * Ce que l'onglet affiche :
 * - `found` : le serveur est dans les mesures ;
 * - `never` : le satellite a répondu, sans ce serveur. `failed` : groupes concernés dont le satellite
 *   n'a pas répondu (le serveur y est peut-être) ;
 * - `unreadable` : aucun satellite concerné n'a répondu (module éteint, satellite d'avant le profileur,
 *   satellite injoignable).
 */
export type InstanceOutcome =
  | { kind: 'found'; match: InstanceMatch }
  | { kind: 'never'; health: Health; failed: GroupResult[] }
  | { kind: 'unreadable'; groups: GroupResult[] };

/**
 * Le serveur de cette instance dans les réponses des satellites.
 *
 * Tous les groupes lus sont fouillés. Les groupes « concernés », ceux dont l'échec est signalé, sont
 * celui de l'instance quand le projet le connaît, sinon tous.
 *
 * @param consoleStatus état d'après la console (running, offline...) : sans mesure à juger, « arrêté »
 *                      complète la phrase, comme sur la page Profileur
 * @param timeZone      fuseau des heures citées (tests) ; omis : celui du navigateur
 */
export function instanceOutcome(results: GroupResult[], instance: InstanceRef, consoleStatus?: string | null, timeZone?: string): InstanceOutcome {
  const instanceGroup = norm(instance.group);
  const found: { group: GroupResult; server: ServerSummary }[] = [];
  for (const group of results) {
    for (const server of group.list?.servers ?? []) {
      if (isInstanceServer(server, instance)) found.push({ group, server });
    }
  }
  // Le groupe de l'instance d'abord, puis le serveur le plus récemment entendu.
  const own = (g: GroupResult) => (instanceGroup != null && g.key === instanceGroup ? 1 : 0);
  found.sort((a, b) => own(b.group) - own(a.group) || b.server.lastSeenAt - a.server.lastSeenAt);
  const status = consoleStatus ?? null;

  const best = found[0];
  if (best) {
    const list = best.group.list!;
    const health = serverHealth(best.server, list.now, timeZone, list.startupGraceSeconds);
    return { kind: 'found', match: { group: best.group, server: best.server, health: withConsole(health, status), now: list.now } };
  }

  const ownGroups = instanceGroup ? results.filter((g) => g.key === instanceGroup) : [];
  const concerned = ownGroups.length > 0 ? ownGroups : results;
  if (concerned.some((g) => g.list)) {
    return { kind: 'never', health: withConsole(NEVER_SEEN, status), failed: concerned.filter((g) => !g.list) };
  }
  return { kind: 'unreadable', groups: concerned };
}

/** Lien « Voir dans le profileur » : la page du serveur quand il a des mesures, sinon la page Profileur. */
export function profilerLink(outcome: InstanceOutcome | null): string {
  if (outcome?.kind !== 'found') return PROFILER_HOME;
  return serverPath({ key: outcome.match.server.key, group: outcome.match.group.key }) ?? PROFILER_HOME;
}
